import { IonButton, IonContent, IonDatetime, IonDatetimeButton, IonHeader, IonItem, IonLabel, IonList, IonModal, IonPage, IonRadio, IonRadioGroup, IonTitle, IonToolbar } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { useMemo, useState } from 'react';
import styles from '../DoorPolicyModal.module.css';
import { useCommonTranslation, useModalTranslation } from '../../hooks/useTranslation';

export interface EditDoorPolicyTimeRangePickerProps {
    timeRange: string;
    onSelectTimeRange: (value: string) => void;
    onClose: () => void;
}

const parseTimeRange = (timeRange: string) => {
    const [start, end] = timeRange.split('-').map((value) => value.trim());
    return {
        start: start || '07:00',
        end: end || '18:00',
    };
};

const EditDoorPolicyTimeRangePicker = ({
    timeRange,
    onSelectTimeRange,
    onClose,
}: EditDoorPolicyTimeRangePickerProps) => {
    const initialRange = useMemo(() => parseTimeRange(timeRange), [timeRange]);
    const [startTime, setStartTime] = useState(initialRange.start);
    const [endTime, setEndTime] = useState(initialRange.end);
    const [timeOption, setTimeOption] = useState<'anyTime' | 'specificTimes'>(
        timeRange.trim() ? 'specificTimes' : 'anyTime'
    );
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();

    const saveSelection = () => {
        if (timeOption === 'anyTime') {
            onSelectTimeRange('');
        } else {
            onSelectTimeRange(`${startTime}-${endTime}`);
        }
        onClose();
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={onClose}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    {/* ios title mode keeps the title centered in md too */}
                    <IonTitle {...({ mode: 'ios' } as object)}>{tModals('door_policy.pickers.time_range.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton strong={true} onClick={saveSelection}>
                            {tCommon('buttons.save')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <section className={`${styles.wrapper} ${styles.section}`}>
                    <h2 className={styles.sectionLabel}>{tModals('door_policy.pickers.time_range.heading')}</h2>
                    <IonList className={styles.listCard}>
                        <IonRadioGroup
                            value={timeOption}
                            onIonChange={(event) => setTimeOption(event.detail.value)}
                        >
                            <IonItem>
                                <IonRadio value="anyTime" labelPlacement="end" justify="start">
                                    {tModals('door_policy.pickers.time_range.any_time')}
                                </IonRadio>
                            </IonItem>
                            <IonItem>
                                <IonRadio value="specificTimes" labelPlacement="end" justify="start">
                                    {tModals('door_policy.pickers.time_range.specific_times')}
                                </IonRadio>
                            </IonItem>
                        </IonRadioGroup>
                        <IonItem>
                            <IonLabel>{tModals('door_policy.pickers.time_range.from')}</IonLabel>
                            <IonDatetimeButton
                                slot="end"
                                datetime="time-range-start"
                                disabled={timeOption === 'anyTime'}
                            />
                        </IonItem>
                        <IonItem>
                            <IonLabel>{tModals('door_policy.pickers.time_range.to')}</IonLabel>
                            <IonDatetimeButton
                                slot="end"
                                datetime="time-range-end"
                                disabled={timeOption === 'anyTime'}
                            />
                        </IonItem>
                    </IonList>
                    <IonModal keepContentsMounted>
                        <IonDatetime
                            id="time-range-start"
                            presentation="time"
                            value={startTime}
                            onIonChange={(event) => setStartTime(event.detail.value as string)}
                        />
                    </IonModal>
                    <IonModal keepContentsMounted>
                        <IonDatetime
                            id="time-range-end"
                            presentation="time"
                            value={endTime}
                            onIonChange={(event) => setEndTime(event.detail.value as string)}
                        />
                    </IonModal>
                </section>
            </IonContent>
        </IonPage>
    );
};

export const EditDoorPolicyTimeRangePickerView = (props: EditDoorPolicyTimeRangePickerProps) => (
    <EditDoorPolicyTimeRangePicker {...props} />
);

export default EditDoorPolicyTimeRangePicker;
